import { createFileRoute } from '@tanstack/react-router'
import { RefreshCw } from 'lucide-react'

import {
  StatisticsCards as AdminStatistics,
  TeamWorkloadWidget,
  CashFlowChartWidget,
  PendingApprovalsWidget,
} from '@/components/admin-dashboard'
import { useAdminDashboard, useRefreshAdminDashboard , useCurrentUser } from '@/hooks'

// 允许访问管理仪表板的角色
const ADMIN_DASHBOARD_ROLES = ['director', 'finance', 'hr', 'pm']

/**
 * 仪表盘页面
 */
export const Route = createFileRoute('/_authenticated/dashboard')({
  component: DashboardPage,
})

function DashboardPage() {
  const { data: user } = useCurrentUser()
  const admin = useAdminDashboard()
  const refreshAdmin = useRefreshAdminDashboard()

  // 当前用户是否有管理权限
  const hasAdminAccess = user && ADMIN_DASHBOARD_ROLES.includes(user.role)
  const hasFinanceAccess = user?.role === 'director' || user?.role === 'finance'

  // 获取当前时间的问候语
  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) {return '早上好'}
    if (hour < 18) {return '下午好'}
    return '晚上好'
  }

  // 格式化当前日期
  const formatDate = () => {
    const now = new Date()
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long',
    }
    return now.toLocaleDateString('zh-CN', options)
  }

  // 刷新处理
  const handleRefresh = () => {
    refreshAdmin()
  }

  const dashboard = admin.data?.data

  return (
    <div className="space-y-6">
      {/* 标题区域 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {getGreeting()}，{user?.name}
          </h1>
          <p className="text-muted-foreground">{formatDate()}</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={admin.isLoading}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border bg-background hover:bg-muted transition-colors disabled:opacity-50"
          title="手动刷新"
        >
          <RefreshCw className={`h-4 w-4 ${admin.isLoading ? 'animate-spin' : ''}`} />
          刷新
        </button>
      </div>

      {/* 管理概览内容 */}
      {hasAdminAccess && (
        <div className="space-y-6">
          {/* 统计卡片 */}
          <AdminStatistics statistics={dashboard?.statistics ?? null} isLoading={admin.isLoading} />

          {/* 主要内容区域 */}
          <div className="grid gap-6 lg:grid-cols-2">
            {/* 团队工时 */}
            <TeamWorkloadWidget workload={dashboard?.workload ?? null} isLoading={admin.isLoading} />

            {/* 现金流趋势（仅 director/finance 可见） */}
            {hasFinanceAccess && (
              <CashFlowChartWidget cashFlow={dashboard?.cashFlow ?? null} isLoading={admin.isLoading} />
            )}

            {/* 待审批 */}
            <PendingApprovalsWidget approvals={dashboard?.approvals ?? null} isLoading={admin.isLoading} />
          </div>

          {/* 错误提示 */}
          {admin.isError && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-center">
              <p className="text-sm text-destructive">加载仪表板数据失败，请稍后重试</p>
            </div>
          )}
        </div>
      )}

      {/* 非管理员显示简单欢迎信息 */}
      {!hasAdminAccess && (
        <div className="rounded-lg border bg-card p-6 text-center">
          <p className="text-muted-foreground">欢迎使用公司管理系统</p>
        </div>
      )}
    </div>
  )
}
